import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Container,
  Card,
  Button,
  Alert,
  Form,
  Spinner,
  Badge,
  Modal,
  Image,
} from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import {
  FaFacebookSquare,
  FaArrowLeft,
  FaPlug,
  FaCheckCircle,
  FaCog,
  FaSync,
} from "react-icons/fa";
import { adminAuthService, adminFacebookService } from "../../../services/api";

const AdminFacebookConnect = () => {
  const navigate = useNavigate();
  const [adminUser, setAdminUser] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertVariant, setAlertVariant] = useState("info");

  // Facebook connection state
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [facebookLoginWindow, setFacebookLoginWindow] = useState(null);
  const [facebookLoginInterval, setFacebookLoginInterval] = useState(null);
  const [facebookProfile, setFacebookProfile] = useState(null);

  const [pages, setPages] = useState([]);

  // Configuration state
  const [currentConfig, setCurrentConfig] = useState(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [configForm, setConfigForm] = useState({
    shopName: "",
    triggerMessage: "สวัสดี",
    refreshThresholdHours: 168,
  });
  const [isSubmittingConfig, setIsSubmittingConfig] = useState(false);

  useEffect(() => {
    const currentAdmin = adminAuthService.getCurrentAdmin();
    setAdminUser(currentAdmin);

    // Load existing configuration and pages
    loadConfiguration();
    loadPages(); // Still load pages to get page info for configuration
    loadFacebookProfile(); // Load Facebook profile if connected

    // Cleanup function for Facebook login resources
    return () => {
      // Clear any Facebook login intervals if they exist
      if (facebookLoginInterval) {
        clearInterval(facebookLoginInterval);
      }

      // Close any Facebook login windows if they exist
      if (facebookLoginWindow && !facebookLoginWindow.closed) {
        facebookLoginWindow.close();
      }
    };
  }, [facebookLoginInterval, facebookLoginWindow]);

  const loadConfiguration = async () => {
    setIsLoadingConfig(true);
    try {
      const response = await adminFacebookService.getConfig();
      if (response.success && response.config) {
        setCurrentConfig(response.config);
        setAlertVariant("info");
        setAlertMessage("พบการตั้งค่า RuaySure ที่มีอยู่แล้ว");
        setShowAlert(true);
      }
    } catch (error) {
      // 404 means no config exists yet, which is fine
      if (error.response?.status !== 404) {
        console.error("Failed to load configuration:", error);
      }
    } finally {
      setIsLoadingConfig(false);
    }
  };

  const loadPages = async () => {
    try {
      const response = await adminFacebookService.getPages();
      console.log("Pages response:", response);
      if (response.success && response.pages) {
        setPages(response.pages);
        setIsConnected(true);
        
        // Load Facebook profile if available from connection data
        if (response.connection && response.connection.facebookId) {
          console.log("Setting Facebook profile from pages response");
          setFacebookProfile({
            name: response.connection.name,
            email: response.connection.email,
            picture: {
              data: {
                url: response.connection.profilePictureUrl,
              },
            },
          });
        }
      }
    } catch (error) {
      console.error("Failed to load pages:", error);
      // 404 means not connected yet
      if (error.response?.status !== 404) {
        console.error("Failed to load pages:", error);
      }
      setIsConnected(false);
    }
  };

  const loadFacebookProfile = async () => {
    try {
      const response = await adminFacebookService.getConnection();
      console.log("Facebook connection response:", response);
      
      // Handle response structure: {success: true, connection: {...}}
      const connectionData = response.connection || response.data?.connection || response;
      console.log("Connection data:", connectionData);
      
      if (connectionData.facebookId) {
        console.log("Setting Facebook profile and connected state");
        setFacebookProfile({
          name: connectionData.name,
          email: connectionData.email,
          picture: {
            data: {
              url: connectionData.profilePictureUrl,
            },
          },
        });
        setIsConnected(true);
      } else {
        console.log("No facebookId found in response");
        setIsConnected(false);
      }
    } catch (error) {
      console.error("Failed to load Facebook profile:", error);
      // 404 means no profile exists yet, which is fine
      if (error.response?.status !== 404) {
        console.error("Failed to load Facebook profile:", error);
      }
      setIsConnected(false);
    }
  };

  const handleFacebookLogin = () => {
    setIsLoading(true);

    // Generate a random state parameter to prevent CSRF attacks
    const state = Math.random().toString(36).substring(2, 15);

    // Store the state in localStorage to verify when the user returns
    localStorage.setItem("facebookLoginState", state);

    // Facebook App ID from environment variable
    const appId = process.env.REACT_APP_FACEBOOK_APP_ID;

    // Redirect URI (must be whitelisted in your Facebook App settings)
    const redirectUri = `${window.location.origin}/facebook-callback`;

    // Permissions to request
    const scope =
      "public_profile,pages_messaging,pages_read_user_content,pages_read_engagement,pages_manage_metadata,page_events,business_management";

    // Construct the Facebook login URL
    const facebookLoginUrl = `https://www.facebook.com/v23.0/dialog/oauth?client_id=${appId}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}&scope=${scope}&response_type=token`;

    // Open a popup window for Facebook login
    const width = 600;
    const height = 700;
    const left = window.innerWidth / 2 - width / 2;
    const top = window.innerHeight / 2 - height / 2;

    const loginWindow = window.open(
      facebookLoginUrl,
      "facebook-login-popup",
      `width=${width},height=${height},left=${left},top=${top}`,
    );

    setFacebookLoginWindow(loginWindow);

    // Set up an interval to check if the popup has been closed or redirected
    const checkLoginInterval = setInterval(() => {
      try {
        // Check if the window was closed
        if (loginWindow.closed) {
          clearInterval(checkLoginInterval);
          setFacebookLoginInterval(null);
          setIsLoading(false);
          return;
        }

        // Try to access the current URL of the popup
        // This will throw an error if the popup has been redirected to a different origin
        const currentUrl = loginWindow.location.href;

        // Check if the URL contains the access token
        if (currentUrl.includes("access_token=")) {
          clearInterval(checkLoginInterval);
          setFacebookLoginInterval(null);

          // Parse the URL to extract the access token and other parameters
          const hashParams = new URLSearchParams(
            currentUrl.substring(currentUrl.indexOf("#") + 1),
          );

          const accessToken = hashParams.get("access_token");
          const expiresIn = hashParams.get("expires_in");
          const receivedState = hashParams.get("state");

          // Verify the state parameter to prevent CSRF attacks
          const originalState = localStorage.getItem("facebookLoginState");

          if (receivedState !== originalState) {
            setAlertVariant("danger");
            setAlertMessage("Security validation failed. Please try again.");
            setShowAlert(true);
            setIsLoading(false);
            loginWindow.close();
            return;
          }

          // Clear the state from localStorage
          localStorage.removeItem("facebookLoginState");

          // Get the Facebook user profile with picture
          fetch(
            `https://graph.facebook.com/v23.0/me?fields=id,name,picture&access_token=${accessToken}`,
          )
            .then((response) => response.json())
            .then((userData) => {
              // Store Facebook profile data
              setFacebookProfile(userData);
              
              // Connect Facebook account to member profile
              const fbData = {
                facebookId: userData.id,
                facebookName: userData.name,
                facebookPicture: userData.picture?.data?.url,
                accessToken: accessToken,
                expiresIn: parseInt(expiresIn, 10),
              };

              return adminFacebookService.connect(fbData);
            })
            .then((response) => {
              // Set Facebook connected state
              setIsConnected(true);

              // Reload pages and configuration after successful connection
              loadPages();
              return loadConfiguration();
            })
            .then(() => {
              setAlertVariant("success");
              setAlertMessage("Successfully connected to Facebook!");
              setShowAlert(true);
            })
            .catch((error) => {
              console.error("Facebook connection error:", error);
              setAlertVariant("danger");

              if (
                error.response &&
                error.response.data &&
                error.response.data.message
              ) {
                setAlertMessage(error.response.data.message);
              } else {
                setAlertMessage(
                  "Failed to connect to Facebook. Please try again.",
                );
              }

              setShowAlert(true);
            })
            .finally(() => {
              setIsLoading(false);
              loginWindow.close();
            });
        }
      } catch (error) {
        // An error will be thrown when the popup is redirected to a different origin
        // This is expected and we can ignore it
      }
    }, 500);

    setFacebookLoginInterval(checkLoginInterval);
  };

  const handleConfigureClick = () => {
    // Use first page from pages list
    if (pages.length > 0) {
      setSelectedPage(pages[0]);
    }
    // Pre-populate form with existing config data
    if (currentConfig) {
      setConfigForm({
        shopName: currentConfig.ruaysureShopName || "",
        triggerMessage: currentConfig.triggerMessage || "สวัสดี",
        refreshThresholdHours: currentConfig.refreshThresholdHours || 168,
      });
    }
    setShowConfigModal(true);
  };

  const handleConfigFormChange = (e) => {
    const { name, value } = e.target;
    setConfigForm({
      ...configForm,
      [name]: value,
    });
  };

  const handleSubmitConfiguration = async (e) => {
    e.preventDefault();

    // Use first page if no page selected
    const pageToUse = selectedPage || (pages.length > 0 ? pages[0] : null);

    if (!pageToUse) {
      setAlertVariant("danger");
      setAlertMessage("ไม่พบเพจ Facebook กรุณาเชื่อมต่อ Facebook ก่อน");
      setShowAlert(true);
      return;
    }

    setIsSubmittingConfig(true);

    try {
      const configData = {
        shopName: configForm.shopName,
        triggerMessage: configForm.triggerMessage,
        refreshThresholdHours: parseInt(configForm.refreshThresholdHours),
      };

      const response = await adminFacebookService.configureRuaysure(configData);

      if (response.success) {
        setAlertVariant("success");
        setAlertMessage("ตั้งค่า RuaySure สำเร็จ!");
        setShowAlert(true);
        setShowConfigModal(false);
        setCurrentConfig(response.config);
      }
    } catch (error) {
      setAlertVariant("danger");
      setAlertMessage(
        error.response?.data?.error || "เกิดข้อผิดพลาดในการตั้งค่า RuaySure"
      );
      setShowAlert(true);
    } finally {
      setIsSubmittingConfig(false);
    }
  };

  const handleRefreshToken = async () => {
    try {
      const response = await adminFacebookService.refreshToken();

      if (response.success) {
        setAlertVariant("success");
        setAlertMessage(response.message || "Refresh token สำเร็จ");
        setShowAlert(true);
        loadConfiguration(); // Reload config to show new expiry
      }
    } catch (error) {
      setAlertVariant("danger");
      setAlertMessage(
        error.response?.data?.error || "เกิดข้อผิดพลาดในการ refresh token"
      );
      setShowAlert(true);
    }
  };

  const handleValidateToken = async () => {
    try {
      const response = await adminFacebookService.validateToken();

      if (response.success) {
        const message = response.refreshed
          ? "Token ถูก refresh อัตโนมัติแล้ว"
          : `Token ยังใช้งานได้ (เหลืออีก ${Math.floor(response.hoursUntilExpiry)} ชั่วโมง)`;

        setAlertVariant(response.valid ? "success" : "warning");
        setAlertMessage(message);
        setShowAlert(true);

        if (response.refreshed) {
          loadConfiguration();
        }
      }
    } catch (error) {
      setAlertVariant("danger");
      setAlertMessage(
        error.response?.data?.error || "เกิดข้อผิดพลาดในการตรวจสอบ token"
      );
      setShowAlert(true);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("คุณแน่ใจหรือไม่ว่าต้องการยกเลิกการเชื่อมต่อ Facebook?")) {
      return;
    }

    try {
      const response = await adminFacebookService.disconnect();

      if (response.success) {
        setAlertVariant("info");
        setAlertMessage(response.message || "ยกเลิกการเชื่อมต่อ Facebook สำเร็จ");
        setShowAlert(true);
        setIsConnected(false);
        setPages([]);
        setFacebookProfile(null);
      }
    } catch (error) {
      setAlertVariant("danger");
      setAlertMessage(
        error.response?.data?.error || "เกิดข้อผิดพลาดในการยกเลิกการเชื่อมต่อ"
      );
      setShowAlert(true);
    }
  };

  return (
    <div className="admin-facebook-connect" style={{ minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      <Container fluid>
        {/* Header */}
        <Row className="bg-white shadow-sm py-3 mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3">
                <Link to="/admin/dashboard">
                  <Button variant="outline-secondary" size="sm">
                    <FaArrowLeft className="me-2" />
                    กลับ
                  </Button>
                </Link>
                <h3 className="mb-0">Facebook Integration</h3>
              </div>
              {adminUser && (
                <div className="text-end">
                  <small className="text-muted d-block">Logged in as</small>
                  <strong>{adminUser.username}</strong>
                </div>
              )}
            </div>
          </Col>
        </Row>

        {/* Main Content */}
        <Container>
          {showAlert && (
            <Alert
              variant={alertVariant}
              onClose={() => setShowAlert(false)}
              dismissible
              className="mb-4"
            >
              {alertMessage}
            </Alert>
          )}

          {/* Connection Status Card */}
          <Row className="mb-4">
            <Col>
              <Card className="shadow-sm">
                <Card.Header>
                  <h4 className="card-title">
                    <FaFacebookSquare className="text-primary me-2" />
                    Facebook Connection
                  </h4>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3" controlId="facebookConnection">
                    <Form.Label>Connect to Facebook</Form.Label>
                    {isConnected && facebookProfile ? (
                      <div className="d-flex align-items-center mb-3">
                        {facebookProfile.picture && (
                          <img
                            src={facebookProfile.picture.data.url}
                            alt={facebookProfile.name}
                            roundedCircle
                            width={50}
                            height={50}
                            className="me-3"
                          />
                        )}
                        <div>
                          <p className="mb-0">
                            <strong>{facebookProfile.name}</strong>
                          </p>
                          <p className="text-muted mb-0">
                            {facebookProfile.email || "Email not available"}
                          </p>
                        </div>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="ms-auto"
                          onClick={handleDisconnect}
                        >
                          Disconnect
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="primary"
                        className="d-flex align-items-center justify-content-center"
                        onClick={handleFacebookLogin}
                        disabled={isLoading}
                      >
                        <FaFacebookSquare className="me-2" size={20} />
                        Connect with Facebook
                      </Button>
                    )}
                    <Form.Text className="text-muted">
                      Connect your Facebook account to manage Facebook pages and integrate with RuaySure.
                    </Form.Text>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Current Configuration */}
          {currentConfig && (
            <Row className="mb-4">
              <Col>
                <Card className="shadow-sm">
                  <Card.Header className="bg-primary text-white">
                    <h5 className="mb-0">
                      <FaCog className="me-2" />
                      Current RuaySure Configuration
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <strong>Page ID:</strong> {currentConfig.ruaysurePageId}
                        </div>
                        <div className="mb-3">
                          <strong>Shop Name:</strong> {currentConfig.ruaysureShopName}
                        </div>
                        <div className="mb-3">
                          <strong>Trigger Message:</strong> {currentConfig.triggerMessage || "N/A"}
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <strong>Token Expires:</strong>{" "}
                          {new Date(currentConfig.tokenExpiresAt).toLocaleString("th-TH")}
                        </div>
                        <div className="mb-3">
                          <strong>Dataset ID:</strong> {currentConfig.datasetId || "N/A"}
                        </div>
                        <div className="mb-3">
                          <strong>Refresh Threshold:</strong> {currentConfig.refreshThresholdHours || 168} hours
                        </div>
                        <div className="mb-3">
                          <strong>Status:</strong>{" "}
                          <Badge bg={currentConfig.isActive ? "success" : "danger"}>
                            {currentConfig.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                      </Col>
                    </Row>
                    <div className="d-flex gap-2 mt-3">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleValidateToken}
                      >
                        <FaCheckCircle className="me-2" />
                        Validate Token
                      </Button>
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={handleRefreshToken}
                      >
                        <FaSync className="me-2" />
                        Refresh Token
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleConfigureClick}
                      >
                        <FaCog className="me-2" />
                        Configure for RuaySure
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </Container>

      {/* Configuration Modal */}
      <Modal
        show={showConfigModal}
        onHide={() => setShowConfigModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Configure RuaySure{selectedPage ? ` for ${selectedPage.name}` : ''}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitConfiguration}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Shop Name *</Form.Label>
              <Form.Control
                type="text"
                name="shopName"
                value={configForm.shopName}
                onChange={handleConfigFormChange}
                placeholder="Enter RuaySure shop name"
                required
              />
              <Form.Text className="text-muted">
                ชื่อร้านค้าที่ใช้ในระบบ RuaySure
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Trigger Message</Form.Label>
              <Form.Control
                type="text"
                name="triggerMessage"
                value={configForm.triggerMessage}
                onChange={handleConfigFormChange}
                placeholder="Enter trigger message (optional)"
              />
              <Form.Text className="text-muted">
                ข้อความที่จะกระตุ้นให้บอทตอบกลับ (ค่าเริ่มต้น: สวัสดี)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Token Refresh Threshold (hours)</Form.Label>
              <Form.Control
                type="number"
                name="refreshThresholdHours"
                value={configForm.refreshThresholdHours}
                onChange={handleConfigFormChange}
                min="1"
                max="720"
              />
              <Form.Text className="text-muted">
                จำนวนชั่วโมงก่อนหมดอายุที่จะ refresh token อัตโนมัติ (ค่าเริ่มต้น: 168 = 7 วัน)
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowConfigModal(false)}
              disabled={isSubmittingConfig}
            >
              ยกเลิก
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={isSubmittingConfig}
            >
              {isSubmittingConfig ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  กำลังบันทึก...
                </>
              ) : (
                "บันทึกการตั้งค่า"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminFacebookConnect;
